import { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  createConfiguration,
  updateConfiguration,
  deleteConfiguration,
  getConfigurations,
} from "../services/configurations/configurations";
import { GlobalConfiguration } from "../interfaces/BDModels";
import { getConfigurationContext } from "../utils";
import { AppContext } from "../context";

type DataResponseType = "DATA" | "ACTIONS";
export type ActionType = "CREATE" | "UPDATE" | "DELETE";

const handleActionFunction = (action: ActionType, t: Function) => {
  switch (action) {
    case "CREATE":
      return {
        actionFn: createConfiguration,
        fnMsg: t("GlobalConfigurations.apiCreateLabel"),
      };
    case "UPDATE":
      return {
        actionFn: updateConfiguration,
        fnMsg: t("GlobalConfigurations.apiUpdateLabel"),
      };
    case "DELETE":
      return {
        actionFn: deleteConfiguration,
        fnMsg: t("GlobalConfigurations.apiDeleteLabel"),
      };
    default:
      return {
        actionFn: () => {},
        fnMsg: "",
      };
  }
};

export const useConfigurations = (type?: DataResponseType, search?: string) => {
  const {
    t,
    toast,
    appState: { selectedApp },
    setAppState,
  } = useContext(AppContext);
  const { pathname } = useLocation();
  const [data, setData] = useState<GlobalConfiguration[] | null>(null);
  const [reload, setReload] = useState<number>(0);
  const appid = selectedApp?.appid || "0";
  const context = search ? search : getConfigurationContext(pathname);

  const reloadFn = () => {
    setData(null);
    setReload((prev) => prev + 1);
  };

  const configurationAction = async (action: ActionType, data: any) => {
    const { actionFn, fnMsg } = handleActionFunction(action, t);
    setAppState((prev) => ({
      ...prev,
      isLoading: true,
    }));
    const actionStatus = await Promise.resolve(actionFn(data, appid))
      .then((response) => {
        toast.current.replace({
          severity: "success",
          summary: "",
          detail: `${fnMsg}`,
          life: 3000,
        });
        return "SUCCESS";
      })
      .catch((error) => {
        toast.current.replace({
          severity: "error",
          summary: "",
          detail: error?.response?.data?.header?.errorMsg ?? t('ConsoleTranslations.generalErrorApiMessage'),
          life: 3000,
        });
        return "ERROR";
      });
    setAppState((prev) => ({
      ...prev,
      isLoading: false,
    }));
    return actionStatus;
  };

  useEffect(() => {
    if (type === "DATA" && appid) {
      (async function () {
        await Promise.resolve(getConfigurations(appid, context))
          .then((response) => {
            setData(response?.data);
          })
          .catch((error) => {
            toast.current.replace({
              severity: "error",
              summary: "",
              detail: error?.response?.data?.header?.errorMsg ?? t('ConsoleTranslations.generalErrorApiMessage'),
              life: 3000,
            });
          });
      })();
    }
  }, [appid, context, toast, type, reload]);

  return {
    data,
    configurationAction,
    reloadFn,
  };
};
